import { useState, useEffect } from 'react';
import { adminApiService } from '../../services/api';
import { AdminReporter, StatePreference } from '../../types';

export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

export const normalizePrimary = (prefs: StatePreference[]): StatePreference[] => {
  if (prefs.length === 0) return [];
  const primaryIdx = prefs.findIndex(p => p.isPrimary === true);
  const chosen = primaryIdx >= 0 ? primaryIdx : 0;
  return prefs.map((p, i) => ({ ...p, isPrimary: i === chosen }));
};

export function useReporterDetailData(reporterId: string) {
  const [reporter, setReporter] = useState<AdminReporter | null>(null);
  const [reporterLoading, setReporterLoading] = useState(true);
  const [reporterError, setReporterError] = useState<string | null>(null);

  const [draftPrefs, setDraftPrefs] = useState<StatePreference[]>([]);
  const [newStateToAdd, setNewStateToAdd] = useState<string>('');
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [statesSnackbar, setStatesSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // TODO RP-296: replace with getAdminReporter(reporterId) once a single-fetch endpoint exists
  const loadReporter = async () => {
    try {
      setReporterLoading(true);
      const res = await adminApiService.getAdminReporters();
      if (res.success && res.data) {
        const found = (res.data as AdminReporter[]).find(r => r.reporterId === reporterId);
        setReporter(found ?? null);
      }
    } catch {
      setReporterError('Failed to load reporter');
    } finally {
      setReporterLoading(false);
    }
  };

  useEffect(() => {
    const fetchReporter = async () => {
      try {
        setReporterLoading(true);
        const res = await adminApiService.getAdminReporters();
        if (res.success && res.data) {
          const found = (res.data as AdminReporter[]).find(r => r.reporterId === reporterId);
          setReporter(found ?? null);
        }
      } catch {
        setReporterError('Failed to load reporter');
      } finally {
        setReporterLoading(false);
      }
    };
    fetchReporter();
  }, [reporterId]);

  useEffect(() => {
    if (reporter) {
      setDraftPrefs(normalizePrimary([...(reporter.statePreferences || [])]));
    }
  }, [reporter]);

  const handleAddStateToDraft = () => {
    if (!newStateToAdd) return;
    if (draftPrefs.some(p => p.state === newStateToAdd)) return;
    const next: StatePreference = {
      state: newStateToAdd,
      virtualAvailable: true,
      inPersonAvailable: true,
      isPrimary: draftPrefs.length === 0,
    };
    setDraftPrefs(normalizePrimary([...draftPrefs, next]));
    setNewStateToAdd('');
  };

  const handleRemoveStateFromDraft = (state: string) => {
    setDraftPrefs(normalizePrimary(draftPrefs.filter(p => p.state !== state)));
  };

  const handleSetPrimaryInDraft = (state: string) => {
    setDraftPrefs(draftPrefs.map(p => ({ ...p, isPrimary: p.state === state })));
  };

  const handleAvailabilityToggle = (state: string, field: 'virtualAvailable' | 'inPersonAvailable', value: boolean) => {
    setDraftPrefs(draftPrefs.map(p => p.state === state ? { ...p, [field]: value } : p));
  };

  const handleSavePrefs = async () => {
    if (!reporter) return;
    setSavingPrefs(true);
    try {
      const response = await adminApiService.setReporterPreferences(reporter.reporterId, draftPrefs);
      if (response.success) {
        setStatesSnackbar({ open: true, message: 'States updated', severity: 'success' });
        await loadReporter();
      } else {
        setStatesSnackbar({ open: true, message: 'Failed to update states', severity: 'error' });
      }
    } catch {
      setStatesSnackbar({ open: true, message: 'Failed to update states', severity: 'error' });
    } finally {
      setSavingPrefs(false);
    }
  };

  const draftValid = draftPrefs.length === 0 || draftPrefs.some(p => p.isPrimary);

  return {
    reporter, reporterLoading, reporterError,
    draftPrefs, newStateToAdd, setNewStateToAdd, savingPrefs, draftValid,
    statesSnackbar, setStatesSnackbar,
    handleAddStateToDraft, handleRemoveStateFromDraft, handleSetPrimaryInDraft,
    handleAvailabilityToggle, handleSavePrefs,
  };
}
